import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    document.addEventListener(ThemeEvents.resultsUpdated, this.#handleResultsUpdated);
    this.setAttribute('initialized', '');
    this.dedupeGroupedProductCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    document.removeEventListener(ThemeEvents.resultsUpdated, this.#handleResultsUpdated);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  #handleResultsUpdated = () => {
    this.dedupeGroupedProductCards();
  };

  dedupeGroupedProductCards() {
    const { grid } = this.refs;

    if (!(grid instanceof HTMLElement)) return;

    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const seenGroupKeys = new Set();

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;

      const groupKey = this.#getProductGroupKey(card);
      if (!groupKey) continue;

      if (seenGroupKeys.has(groupKey)) {
        card.remove();
        continue;
      }

      seenGroupKeys.add(groupKey);
    }

    this.#updateVisibleProductsCount();
  }

  /**
   * @param {HTMLElement} card
   * @returns {string}
   */
  #getProductGroupKey(card) {
    const connectedProductUrls = card.querySelectorAll('combined-listing-card-swatches button[data-connected-product-url]');
    const groupedPathnames = Array.from(connectedProductUrls, (button) =>
      this.#normalizePathname(button.getAttribute('data-connected-product-url'))
    ).filter(Boolean);

    if (groupedPathnames.length > 0) {
      return `group:${Array.from(new Set(groupedPathnames)).sort().join('|')}`;
    }

    const productLink = card.querySelector('a.product-card__link[href]');
    const productPathname = this.#normalizePathname(productLink?.getAttribute('href'));

    return productPathname ? `product:${productPathname}` : '';
  }

  /**
   * @param {string | null | undefined} url
   * @returns {string}
   */
  #normalizePathname(url) {
    if (!url) return '';

    try {
      const pathname = new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
      return pathname || '/';
    } catch {
      return '';
    }
  }

  #updateVisibleProductsCount() {
    const { grid } = this.refs;

    if (!(grid instanceof HTMLElement)) return;

    const visibleCount = grid.querySelectorAll(':scope > [ref="cards[]"]').length;
    const countText = this.#formatVisibleProductsCount(visibleCount);

    if (!countText) return;

    const countStatuses = this.querySelectorAll('[data-testid="products-count"] [role="status"]');

    for (const countStatus of countStatuses) {
      countStatus.textContent = countText;
    }
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #formatVisibleProductsCount(count) {
    const template =
      count === 1
        ? Theme?.translations?.item_count_one || '{{ count }} item'
        : Theme?.translations?.item_count_other || '{{ count }} items';

    return template.replace('{{ count }}', String(count));
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
